import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SavedSearches = ({ savedSearches, onCreateAlert, onDeleteSearch, onRunSearch }) => {
  const [isCreatingAlert, setIsCreatingAlert] = useState(false);
  const [alertName, setAlertName] = useState('');

  const handleCreateAlert = () => {
    if (alertName.trim()) {
      onCreateAlert(alertName);
      setAlertName('');
      setIsCreatingAlert(false);
    }
  };

  const mockSavedSearches = [
    {
      id: 1,
      name: "2 BHK in Koramangala",
      filters: {
        location: "Koramangala",
        propertyType: "2bhk",
        priceRange: "₹20,000 - ₹35,000",
        amenities: ["Parking", "Gym"]
      },
      alertsEnabled: true,
      lastRun: "2 hours ago",
      newResults: 3,
      totalResults: 24
    },
    {
      id: 2,
      name: "Furnished Studio Near Metro",
      filters: {
        location: "Whitefield",
        propertyType: "studio",
        priceRange: "₹15,000 - ₹25,000",
        amenities: ["Metro", "Furnished"]
      },
      alertsEnabled: false,
      lastRun: "1 day ago",
      newResults: 0,
      totalResults: 12
    },
    {
      id: 3,
      name: "Villa in Electronic City",
      filters: {
        location: "Electronic City",
        propertyType: "villa",
        priceRange: "₹50,000 - ₹80,000",
        amenities: ["Garden", "Security"]
      },
      alertsEnabled: true,
      lastRun: "3 hours ago",
      newResults: 1,
      totalResults: 8
    }
  ];

  return (
    <div className="bg-surface border border-border rounded-lg shadow-property-card">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Bookmark" size={20} className="text-primary" />
          <h3 className="font-semibold text-text-primary">Saved Searches</h3>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={() => setIsCreatingAlert(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Create Alert
        </Button>
      </div>

      {/* Create Alert Form */}
      {isCreatingAlert && (
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="space-y-3">
            <Input
              label="Alert Name"
              placeholder="Enter a name for this search alert"
              value={alertName}
              onChange={(e) => setAlertName(e.target.value)}
            />
            <div className="flex space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleCreateAlert}
                iconName="Check"
                iconPosition="left"
              >
                Create Alert
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsCreatingAlert(false);
                  setAlertName('');
                }}
                iconName="X"
                iconPosition="left"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Saved Searches List */}
      <div className="p-4">
        {mockSavedSearches.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-3" />
            <h4 className="font-medium text-text-primary mb-2">No saved searches yet</h4>
            <p className="text-sm text-text-secondary mb-4">
              Save your searches to get notified about new properties
            </p>
            <Button
              variant="default"
              onClick={() => setIsCreatingAlert(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Create Your First Alert
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {mockSavedSearches.map((search) => (
              <div
                key={search.id}
                className="border border-border rounded-lg p-4 hover:shadow-property-card transition-smooth"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-text-primary mb-1">{search.name}</h4>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                        {search.filters.location}
                      </span>
                      <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded-full">
                        {search.filters.propertyType.toUpperCase()}
                      </span>
                      <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                        {search.filters.priceRange}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRunSearch(search.id)}
                      iconName="Play"
                    >
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteSearch(search.id)}
                      iconName="Trash2"
                    >
                    </Button>
                  </div>
                </div>

                {/* Search Stats */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary">Last run: {search.lastRun}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Search" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary">{search.totalResults} results</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {search.newResults > 0 && (
                      <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">
                        {search.newResults} new
                      </span>
                    )}
                    <div className="flex items-center space-x-1">
                      <Icon 
                        name={search.alertsEnabled ? "Bell" : "BellOff"} 
                        size={14} 
                        className={search.alertsEnabled ? "text-success" : "text-text-secondary"} 
                      />
                      <span className={`text-xs ${search.alertsEnabled ? "text-success" : "text-text-secondary"}`}>
                        {search.alertsEnabled ? "Alerts ON" : "Alerts OFF"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Amenities */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {search.filters.amenities.map((amenity, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full"
                    >
                      {amenity}
                    </span>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRunSearch(search.id)}
                    iconName="Search"
                    iconPosition="left"
                  >
                    Run Search
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    iconPosition="left"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName={search.alertsEnabled ? "BellOff" : "Bell"}
                    iconPosition="left"
                  >
                    {search.alertsEnabled ? "Disable" : "Enable"} Alerts
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedSearches;